// Rule-based engine for the AI Flags pill.
//
// Two rule kinds:
//   • ComparisonRule — "AI value vs application value, flag if they disagree."
//     A rule has multiple SOURCES (e.g. CDL doc, DOT Med doc, MVR scalar field
//     all validating the medical expiration); each source that mismatches the
//     application contributes to the flag's tooltip.
//   • ExistenceRule — "this document contains a flag-worthy thing." No
//     application analog — the presence of (e.g.) a suspension on an MVR is
//     itself the flag. Used today for MVR accidents/violations/etc.; ready to
//     extend to PSP inspections by adding a new source kind.
//
// Each rule produces zero or more `Flag` objects. The pill groups flags by
// `group` and renders them: comparison flags collapse to one row per rule with
// a multi-source tooltip; existence flags aggregate by `noun` into the count
// subtitle and each flag adds a tooltip row.

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { DocumentCategory } from 'src/graphql/types/documents'
import {
  ExtractedFindingDetail,
  MvrExtractedFinding,
  valuesMismatch,
} from 'src/lib/documentIntelligence'
import { isSameUtcDay } from 'src/utils/date/toMonthYearKey'

import { getUtcMonth } from '../../utils'
import { isLicenseNumberMismatch } from '../licenseNumberComparison'

import { incidentFactDetails } from './incidentFactDetails'
import { mvrFindingTypeLabel as singularMvrLabel } from './mvrFindingTypeLabel'

dayjs.extend(utc)

// ── Group keys ──────────────────────────────────────────────────────────────

export type FlagGroupKey = 'cdl' | 'medical' | 'mvr' | 'psp'

// ── License values (application side) ───────────────────────────────────────

export interface LicenseValues {
  licenseNumber?: string | null
  state?: string | null
  licenseClass?: string | null
  endorsements?: string[] | null
  expirationDate?: string | null
  medicalExamExpirationDate?: string | null
  // Driver's answer to "Have they ever had any license, permit, or privilege
  // been suspended, revoked, or denied?" — null when unanswered. Compared
  // against the MVR's SUSPENSION / LICENSE_ACTION findings; a "No" answer
  // when the MVR shows otherwise drives the License card chip (ENG-4273).
  isLicenseSuspended?: boolean | null
}

// ── Prediction shape (CDL/medical doc predictions) ─────────────────────────

export interface ExtractedPrediction {
  field: string
  confidence?: number | null
  expirationValue?: string | null
  textValue?: string | null
  categoryValue?: string | null
  reviewDecision?: string | null
}

export interface PredictionEntry {
  prediction: ExtractedPrediction
  documentId: string
}

// ── MVR scalar row (e.g. MVR_MEDICAL_CERT_EXPIRATION) ───────────────────────

export interface MvrScalarRow {
  field: string
  value?: string | null
}

// ── Tooltip row shape (existence rules emit these per matching finding) ─────

export interface TooltipRowData {
  key: string
  label: string
  detail?: string
  description?: string | null
  details?: ExtractedFindingDetail[]
}

// ── Rule types ──────────────────────────────────────────────────────────────

export type Comparator = 'date' | 'string' | 'list' | 'license-number'

// Where a comparison rule reads its AI-extracted value from.
export type ComparisonSource =
  // CDL / DOT Medical document prediction.
  | {
      kind: 'document'
      docCategory: DocumentCategory
      predictionField: string
      valueExtractor: 'textValue' | 'expirationValue' | 'categoryValue'
      sourceLabel: string
    }
  // A scalar row on the MVR's aiExtractedRows (e.g. MVR_MEDICAL_CERT_EXPIRATION).
  | {
      kind: 'mvr-scalar'
      rowField: string
      sourceLabel: string
    }

export interface ComparisonRule {
  kind: 'comparison'
  key: string
  group: FlagGroupKey
  label: string
  comparator: Comparator
  applicationValue: (
    license: LicenseValues | null | undefined
  ) => string | string[] | null | undefined
  applicationFallback: string
  formatValue: (raw: string) => string
  sources: ComparisonSource[]
}

export interface ExistenceRule {
  kind: 'existence'
  key: string
  group: FlagGroupKey
  // MVR findings come from ctx.mvrFindings; PSP findings come from
  // ctx.pspFindings. The two share the same flattened MvrExtractedFinding shape
  // (they both come off the backend's aiExtractedRows read-model) but read
  // from separate document lists, so the rules explicitly declare which source
  // they mean rather than mixing them under one array.
  source: 'mvr-finding' | 'psp-finding'
  matches: (finding: MvrExtractedFinding) => boolean
  noun: { singular: string; plural: string }
  buildTooltipRow: (finding: MvrExtractedFinding) => TooltipRowData
  // When true, this rule's count adds to the "report shows more incidents than
  // the driver disclosed" red-dot signal in the renderer. Both MVR and PSP
  // rules can set this — the renderer sums across sources.
  contributesToIncidentCount?: boolean
}

// ── ContradictedAbsenceRule ─────────────────────────────────────────────────
//
// "The driver said they DIDN'T, but the AI found that they DID." The
// application carries a boolean answer (e.g. "Have you ever had your license
// suspended?"); the AI extracted records that contradict a "No" answer.
//
// Unlike ComparisonRule (compare two scalar values), the AI side here is the
// EXISTENCE of records matching a predicate, not a scalar value extraction.
// Unlike ExistenceRule (count records and emit a per-finding flag), this rule
// only emits ONE flag — gated by the application answer — and renders as a
// single chip on the form field, not a list of per-finding rows.
//
// The flag is emitted as a ComparisonFlag so the per-field chip pipeline
// (`AiMismatchChip`, License card mismatches map) reuses the same shape.
export interface ContradictedAbsenceRule {
  kind: 'contradicted-absence'
  key: string
  group: FlagGroupKey
  label: string
  // Pulls the driver's boolean answer off the application.
  applicationValue: (
    license: LicenseValues | null | undefined
  ) => boolean | null | undefined
  // Display labels for the two boolean states. The application value the chip
  // reports is whichever the driver answered.
  yesLabel: string
  noLabel: string
  // Selects which MVR records contradict a "No" answer.
  matches: (finding: MvrExtractedFinding) => boolean
  // Summarizes the matching records for the chip's AI-value line, e.g.
  // ([SUSPENSION, LICENSE_ACTION, LICENSE_ACTION]) => "1 suspension, 2 license actions".
  formatExistence: (findings: MvrExtractedFinding[]) => string
  sourceLabel: string
}

export type Rule = ComparisonRule | ExistenceRule | ContradictedAbsenceRule

// ── Engine output (Flag) ────────────────────────────────────────────────────

export interface MismatchSource {
  sourceLabel: string
  aiValue: string
  documentId: string | null
  // Set for document-source mismatches — used by the License card chips to
  // call REVIEW_DOCUMENT_PREDICTIONS with the correct PredictionField when the
  // recruiter dismisses a chip. null for mvr-scalar sources (no dismiss path).
  predictionField: string | null
}

export interface ComparisonFlag {
  kind: 'comparison'
  key: string
  group: FlagGroupKey
  label: string
  applicationValue: string
  mismatches: MismatchSource[]
}

export interface ExistenceFlag {
  kind: 'existence'
  key: string
  group: FlagGroupKey
  noun: { singular: string; plural: string }
  tooltipRow: TooltipRowData
  documentId: string | null
  contributesToIncidentCount: boolean
}

export type Flag = ComparisonFlag | ExistenceFlag

// ── Evaluation context ──────────────────────────────────────────────────────

export interface EvaluationContext {
  license: LicenseValues | null | undefined
  // Best CDL prediction per field across completed CDL documents.
  cdlPredictions: Record<string, PredictionEntry>
  // Best DOT_MEDICAL prediction per field across completed DOT_MEDICAL documents.
  medicalPredictions: Record<string, PredictionEntry>
  // Scalar rows on the selected MVR — used for 'mvr-scalar' sources. Empty
  // when no completed MVR carries the rows the rules read.
  mvrScalarRows: MvrScalarRow[]
  // documentId of the MVR holding the scalar rows above; null if absent.
  mvrScalarDocumentId: string | null
  // Findings from the selected MVR — used for existence rules. Driven by the
  // same selection logic the rest of AiFlagsPill uses.
  mvrFindings: MvrExtractedFinding[]
  mvrFindingsDocumentId: string | null
  // ENG-4335: Findings from the selected PSP document — used for the
  // psp-finding existence rules. Optional so consumers that don't care about
  // PSP (LicenseInformationCard, StepFindingsBadge) don't have to plumb
  // empty PSP context through — the evaluator defaults omitted fields to
  // the empty state. Non-admin callers on the applicant page also pass
  // nothing here because PSP is an admin-only rollout.
  pspFindings?: MvrExtractedFinding[]
  pspFindingsDocumentId?: string | null
}

// ── Engine ──────────────────────────────────────────────────────────────────

export function evaluateRules(rules: Rule[], ctx: EvaluationContext): Flag[] {
  const flags: Flag[] = []
  for (const rule of rules) {
    if (rule.kind === 'comparison') {
      const flag = evaluateComparisonRule(rule, ctx)
      if (flag) flags.push(flag)
    } else if (rule.kind === 'existence') {
      flags.push(...evaluateExistenceRule(rule, ctx))
    } else {
      const flag = evaluateContradictedAbsenceRule(rule, ctx)
      if (flag) flags.push(flag)
    }
  }
  return flags
}

function evaluateComparisonRule(
  rule: ComparisonRule,
  ctx: EvaluationContext
): ComparisonFlag | null {
  const appValue = rule.applicationValue(ctx.license)
  const mismatches: MismatchSource[] = []
  for (const source of rule.sources) {
    const sourceData = readComparisonSource(source, ctx)
    if (!sourceData?.value) continue
    if (mismatchesByComparator(rule.comparator, sourceData.value, appValue)) {
      mismatches.push({
        sourceLabel: source.sourceLabel,
        aiValue: rule.formatValue(sourceData.value),
        documentId: sourceData.documentId,
        predictionField:
          source.kind === 'document' ? source.predictionField : null,
      })
    }
  }
  if (mismatches.length === 0) return null
  return {
    kind: 'comparison',
    key: rule.key,
    group: rule.group,
    label: rule.label,
    applicationValue: formatApplicationValue(rule, appValue),
    mismatches,
  }
}

function evaluateExistenceRule(
  rule: ExistenceRule,
  ctx: EvaluationContext
): ExistenceFlag[] {
  // pspFindings / pspFindingsDocumentId are optional on EvaluationContext
  // (see the type) — callers outside AiFlagsPill (LicenseInformationCard,
  // StepFindingsBadge) don't plumb PSP context through. Default to the
  // empty state so a psp-finding rule silently produces no flags for
  // those callers instead of throwing `.filter` on undefined.
  const findings =
    rule.source === 'mvr-finding' ? ctx.mvrFindings : (ctx.pspFindings ?? [])
  const documentId =
    rule.source === 'mvr-finding'
      ? ctx.mvrFindingsDocumentId
      : (ctx.pspFindingsDocumentId ?? null)
  return findings
    .filter((f) => rule.matches(f))
    .map((f) => ({
      kind: 'existence',
      key: `${rule.key}-${f.key}`,
      group: rule.group,
      noun: rule.noun,
      tooltipRow: rule.buildTooltipRow(f),
      documentId,
      contributesToIncidentCount: rule.contributesToIncidentCount ?? false,
    }))
}

function evaluateContradictedAbsenceRule(
  rule: ContradictedAbsenceRule,
  ctx: EvaluationContext
): ComparisonFlag | null {
  // Only fire when the driver answered No. Unanswered (null/undefined) or a
  // Yes answer means there's nothing to contradict.
  if (rule.applicationValue(ctx.license) !== false) return null
  const matching = ctx.mvrFindings.filter(rule.matches)
  if (matching.length === 0) return null
  return {
    kind: 'comparison',
    key: rule.key,
    group: rule.group,
    label: rule.label,
    applicationValue: rule.noLabel,
    mismatches: [
      {
        sourceLabel: rule.sourceLabel,
        aiValue: rule.formatExistence(matching),
        documentId: ctx.mvrFindingsDocumentId,
        // MVR records aren't document predictions — no
        // REVIEW_DOCUMENT_PREDICTIONS path. Consumers hide the dismiss × on
        // sources with null predictionField.
        predictionField: null,
      },
    ],
  }
}

function readComparisonSource(
  source: ComparisonSource,
  ctx: EvaluationContext
): { value: string | null; documentId: string | null } | null {
  if (source.kind === 'document') {
    const preds =
      source.docCategory === DocumentCategory.CDL
        ? ctx.cdlPredictions
        : source.docCategory === DocumentCategory.DOT_MEDICAL
          ? ctx.medicalPredictions
          : null
    if (!preds) return null
    const entry = preds[source.predictionField]
    if (!entry) return null
    const value =
      source.valueExtractor === 'textValue'
        ? entry.prediction.textValue
        : source.valueExtractor === 'expirationValue'
          ? entry.prediction.expirationValue
          : entry.prediction.categoryValue
    return { value: value ?? null, documentId: entry.documentId }
  }
  if (source.kind === 'mvr-scalar') {
    const row = ctx.mvrScalarRows.find((r) => r.field === source.rowField)
    if (!row?.value) return null
    return { value: row.value, documentId: ctx.mvrScalarDocumentId }
  }
  return null
}

function mismatchesByComparator(
  comparator: Comparator,
  aiValue: string,
  appValue: string | string[] | null | undefined
): boolean {
  if (comparator === 'date') {
    if (!appValue || Array.isArray(appValue)) return true
    return !isSameUtcDay(aiValue, appValue)
  }
  if (comparator === 'string') {
    if (!appValue || Array.isArray(appValue)) return true
    return aiValue.trim().toUpperCase() !== appValue.trim().toUpperCase()
  }
  if (comparator === 'list') {
    const list = Array.isArray(appValue) ? appValue : []
    if (list.length === 0) return true
    return valuesMismatch('LICENSE_ENDORSEMENTS', aiValue, list.join(','))
  }
  if (comparator === 'license-number') {
    const applicantValue = Array.isArray(appValue) ? '' : appValue ?? ''
    return isLicenseNumberMismatch({
      extractedLicenseNumber: aiValue,
      applicantLicenseNumber: applicantValue,
    })
  }
  return false
}

function formatApplicationValue(
  rule: ComparisonRule,
  appValue: string | string[] | null | undefined
): string {
  if (appValue == null) return rule.applicationFallback
  if (Array.isArray(appValue)) {
    return appValue.length > 0 ? appValue.join(', ') : rule.applicationFallback
  }
  return rule.formatValue(appValue)
}

// ── Context-builders (shared with consumers of the engine) ─────────────────
//
// Every surface that consumes the rule engine (AiFlagsPill, License card
// chips, checklist badge) needs to assemble an EvaluationContext from raw
// GraphQL data. These helpers keep that assembly consistent across surfaces —
// without them, each surface would re-implement the prediction merge logic and
// drift apart.

// "Has a value" rules, kept byte-for-byte aligned with LicenseInformationCard
// so every surface picks the SAME winning prediction:
//   • CDL merge considers textValue/expirationValue/categoryValue (front+back
//     may both be uploaded; non-empty fields win)
//   • Medical merge considers expirationValue only (the only field of interest
//     on a DOT Medical card)
export const cdlHasValue = (p: ExtractedPrediction): boolean =>
  !!(p.textValue?.trim() || p.expirationValue || p.categoryValue)
export const medicalHasValue = (p: ExtractedPrediction): boolean =>
  !!p.expirationValue

// Picks the best prediction PER FIELD across all completed documents of a
// category. A driver may upload multiple cards (front/back, or a renewal), and
// a later upload may not have had a field extracted — so we can't just take
// the first. Priority mirrors LicenseInformationCard's cdlPredLookup: a
// present value beats an empty one (per the category's `hasValue` rule), then
// higher confidence wins. Dismissed (KEPT_ORIGINAL) predictions are ignored
// so a resolved flag doesn't reappear.
export const bestPredictionsByField = (
  docs: Array<{
    documentId: string
    documentIntelligence?: { predictions?: ExtractedPrediction[] | null } | null
  }>,
  hasValue: (p: ExtractedPrediction) => boolean
): Record<string, PredictionEntry> => {
  const byField: Record<string, PredictionEntry> = {}
  for (const doc of docs) {
    for (const prediction of doc.documentIntelligence?.predictions ?? []) {
      if (prediction.reviewDecision === 'KEPT_ORIGINAL') continue

      const existing = byField[prediction.field]
      if (!existing) {
        byField[prediction.field] = { prediction, documentId: doc.documentId }
        continue
      }

      const candidateHasValue = hasValue(prediction)
      const existingHasValue = hasValue(existing.prediction)
      if (candidateHasValue !== existingHasValue) {
        if (candidateHasValue)
          byField[prediction.field] = { prediction, documentId: doc.documentId }
      } else if (
        (prediction.confidence ?? -1) > (existing.prediction.confidence ?? -1)
      ) {
        byField[prediction.field] = { prediction, documentId: doc.documentId }
      }
    }
  }
  return byField
}

// ── Rule-key → application form-field name mapping ─────────────────────────
//
// Consumers that render PER-FIELD (License card chips, future per-field UI)
// need to look up a flag by the form-field it corresponds to. The rules
// themselves use keys like 'cdl-license-number' that are scoped to the rule
// engine; this mapping translates to the form-field names the License card
// component knows.
export const RULE_KEY_TO_FORM_FIELD: Record<string, string> = {
  'cdl-license-number': 'licenseNumber',
  'cdl-state': 'state',
  'cdl-class': 'licenseClass',
  'cdl-endorsements': 'endorsements',
  'cdl-expiration': 'expirationDate',
  'medical-expiration': 'medicalExamExpirationDate',
  'license-ever-suspended': 'isLicenseSuspended',
}

// ── Default formatters ──────────────────────────────────────────────────────

const identity = (v: string) => v

// Date formatter aligned with the rest of the pill — kept here so rules
// reference one consistent helper.
const formatDate = (value?: string | null) =>
  value ? dayjs.utc(value).format('MM/DD/YYYY') : 'Not set'

// ── MVR existence-rule helpers ──────────────────────────────────────────────

// Tooltip row for a records-based MVR finding (accident, violation, suspension,
// etc.). Mirrors the per-finding rendering AiFlagsPill used pre-engine so the
// migration is byte-for-byte for any MVR doc that has accidents/violations.
const mvrFindingTooltipRow = (f: MvrExtractedFinding): TooltipRowData => ({
  key: f.key,
  label: f.location || singularMvrLabel(f.findingType),
  detail: `${singularMvrLabel(f.findingType)} · ${getUtcMonth(f.date, '—')}`,
  description: f.description,
  details: [...f.details, ...incidentFactDetails(f)],
})

// ── PSP existence-rule helpers (ENG-4335) ──────────────────────────────────

// Singular label for a PSP finding type — mvrFindingTypeLabel has no cases for
// PSP so we'd fall through to the raw enum ("CRASH") without this. Kept
// inline (not shared with the PspFindingsPill component's local
// findingTypeLabel) so the AiFlags rule engine doesn't reach into a
// component's private helpers.
const pspFindingTypeLabel = (findingType: string): string => {
  switch (findingType) {
    case 'CRASH':
      return 'PSP crash'
    case 'VIOLATION':
      return 'PSP violation'
    case 'INSPECTION':
      return 'PSP inspection'
    default:
      return findingType
  }
}

// Tooltip row for a records-based PSP finding. Mirrors mvrFindingTooltipRow
// now that ENG-4406 populates `location` for all PSP finding types (VIOLATION
// via parent-inspection join; INSPECTION/CRASH via their own report state).
// So the layout matches MVR: bold state as the label, type + date on the
// secondary line, description on its own line below. pspFindingTypeLabel
// handles CRASH/VIOLATION/INSPECTION (mvrFindingTypeLabel would emit raw
// enum strings for those). incidentFactDetails is safe to reuse — for PSP
// records the cmvInvolved / injuries / fatalities booleans are always null.
const pspFindingTooltipRow = (f: MvrExtractedFinding): TooltipRowData => ({
  key: f.key,
  label: f.location || pspFindingTypeLabel(f.findingType),
  detail: `${pspFindingTypeLabel(f.findingType)} · ${getUtcMonth(f.date, '—')}`,
  description: f.description,
  details: [...f.details, ...incidentFactDetails(f)],
})

// ── The rules ───────────────────────────────────────────────────────────────

export const FLAG_RULES: Rule[] = [
  // ── CDL field comparisons ─────────────────────────────────────────────────
  {
    kind: 'comparison',
    key: 'cdl-license-number',
    group: 'cdl',
    label: 'License #',
    comparator: 'license-number',
    applicationValue: (l) => l?.licenseNumber,
    applicationFallback: 'Not set',
    formatValue: identity,
    sources: [
      {
        kind: 'document',
        docCategory: DocumentCategory.CDL,
        predictionField: 'LICENSE_NUMBER',
        valueExtractor: 'textValue',
        sourceLabel: 'CDL',
      },
    ],
  },
  {
    kind: 'comparison',
    key: 'cdl-state',
    group: 'cdl',
    label: 'State',
    comparator: 'string',
    applicationValue: (l) => l?.state,
    applicationFallback: 'Not set',
    formatValue: identity,
    sources: [
      {
        kind: 'document',
        docCategory: DocumentCategory.CDL,
        predictionField: 'LICENSE_STATE',
        valueExtractor: 'textValue',
        sourceLabel: 'CDL',
      },
    ],
  },
  {
    kind: 'comparison',
    key: 'cdl-class',
    group: 'cdl',
    label: 'Class',
    comparator: 'string',
    applicationValue: (l) => l?.licenseClass,
    applicationFallback: 'Not set',
    formatValue: identity,
    sources: [
      {
        kind: 'document',
        docCategory: DocumentCategory.CDL,
        predictionField: 'LICENSE_CLASS',
        valueExtractor: 'textValue',
        sourceLabel: 'CDL',
      },
    ],
  },
  {
    kind: 'comparison',
    key: 'cdl-endorsements',
    group: 'cdl',
    label: 'Endorsements',
    comparator: 'list',
    applicationValue: (l) => l?.endorsements,
    applicationFallback: 'Not set',
    formatValue: identity,
    sources: [
      {
        kind: 'document',
        docCategory: DocumentCategory.CDL,
        predictionField: 'LICENSE_ENDORSEMENTS',
        valueExtractor: 'textValue',
        sourceLabel: 'CDL',
      },
    ],
  },
  {
    kind: 'comparison',
    key: 'cdl-expiration',
    group: 'cdl',
    label: 'Expiration',
    comparator: 'date',
    applicationValue: (l) => l?.expirationDate,
    applicationFallback: 'Not set',
    formatValue: formatDate,
    sources: [
      {
        kind: 'document',
        docCategory: DocumentCategory.CDL,
        predictionField: 'DATE',
        valueExtractor: 'expirationValue',
        sourceLabel: 'CDL',
      },
    ],
  },

  // ── Medical expiration: cross-validated across DOT Med + MVR ──────────────
  // ENG-4165: the MVR-extracted medical cert expiration is a third source
  // alongside the DOT Medical doc. When two sources disagree with the app, the
  // tooltip lists both ("DOT Med says …, MVR says …, Your app: …").
  {
    kind: 'comparison',
    key: 'medical-expiration',
    group: 'medical',
    label: 'Expiration',
    comparator: 'date',
    applicationValue: (l) => l?.medicalExamExpirationDate,
    applicationFallback: 'Not set',
    formatValue: formatDate,
    sources: [
      {
        kind: 'document',
        docCategory: DocumentCategory.DOT_MEDICAL,
        predictionField: 'DATE',
        valueExtractor: 'expirationValue',
        sourceLabel: 'DOT Med',
      },
      {
        kind: 'mvr-scalar',
        rowField: 'MVR_MEDICAL_CERT_EXPIRATION',
        sourceLabel: 'MVR',
      },
    ],
  },

  // ── MVR existence flags ────────────────────────────────────────────────
  // These were previously hard-coded in AiFlagsPill's `mvr` group construction.
  // Now they're declared as data — adding a new finding type or a new document
  // type (PSP inspections, etc.) is a one-entry addition here, not a code
  // change in the engine or the renderer.
  {
    kind: 'existence',
    key: 'mvr-accidents',
    group: 'mvr',
    source: 'mvr-finding',
    matches: (f) => f.findingType === 'ACCIDENT',
    noun: { singular: 'accident', plural: 'accidents' },
    buildTooltipRow: mvrFindingTooltipRow,
    contributesToIncidentCount: true,
  },
  {
    kind: 'existence',
    key: 'mvr-violations',
    group: 'mvr',
    source: 'mvr-finding',
    matches: (f) =>
      f.findingType === 'VIOLATION_MOVING' ||
      f.findingType === 'VIOLATION_NON_MOVING',
    noun: { singular: 'violation', plural: 'violations' },
    buildTooltipRow: mvrFindingTooltipRow,
    contributesToIncidentCount: true,
  },
  {
    kind: 'existence',
    key: 'mvr-disqualifications',
    group: 'mvr',
    source: 'mvr-finding',
    matches: (f) => f.findingType === 'DISQUALIFICATION',
    noun: { singular: 'disqualification', plural: 'disqualifications' },
    buildTooltipRow: mvrFindingTooltipRow,
  },
  {
    kind: 'existence',
    key: 'mvr-suspensions',
    group: 'mvr',
    source: 'mvr-finding',
    matches: (f) => f.findingType === 'SUSPENSION',
    noun: { singular: 'suspension', plural: 'suspensions' },
    buildTooltipRow: mvrFindingTooltipRow,
  },
  {
    kind: 'existence',
    key: 'mvr-license-actions',
    group: 'mvr',
    source: 'mvr-finding',
    matches: (f) => f.findingType === 'LICENSE_ACTION',
    noun: { singular: 'license action', plural: 'license actions' },
    buildTooltipRow: mvrFindingTooltipRow,
  },
  // ── PSP existence flags (ENG-4335) ─────────────────────────────────────────
  // Same shape as MVR rules but read from ctx.pspFindings. CRASH and VIOLATION
  // both feed the incidents-count rollup — an applicant with a PSP crash or
  // violation that isn't matched against a disclosed Accident row on the
  // application drives the AiFlagsPill red dot (mirrors the applicant page's
  // existing MVR rule: `incidentCount > disclosedAccidentCount`).
  // INSPECTION is intentionally omitted from the incident-count rollup — it's
  // not a self-disclosable event, so it never contradicts the application.
  // It's also omitted as a plain flag: the PSP surface is admin-only for
  // rollout and cluttering the popover with clean inspections would drown
  // out the actionable signals.
  {
    kind: 'existence',
    key: 'psp-crashes',
    group: 'psp',
    source: 'psp-finding',
    matches: (f) => f.findingType === 'CRASH',
    noun: { singular: 'PSP crash', plural: 'PSP crashes' },
    buildTooltipRow: pspFindingTooltipRow,
    contributesToIncidentCount: true,
  },
  {
    kind: 'existence',
    key: 'psp-violations',
    group: 'psp',
    source: 'psp-finding',
    matches: (f) => f.findingType === 'VIOLATION',
    noun: { singular: 'PSP violation', plural: 'PSP violations' },
    buildTooltipRow: pspFindingTooltipRow,
    contributesToIncidentCount: true,
  },
  // ── ENG-4273: "Have they ever been suspended/revoked/denied?" ─────────────
  // Driver answers No on the License card's question, but the MVR shows a
  // SUSPENSION or LICENSE_ACTION. Emits a single ComparisonFlag that the
  // License card renders as a chip next to the question label.
  {
    kind: 'contradicted-absence',
    key: 'license-ever-suspended',
    group: 'cdl',
    label: 'License ever suspended/revoked/denied',
    applicationValue: (license) => license?.isLicenseSuspended,
    yesLabel: 'Yes',
    noLabel: 'No',
    matches: (f) =>
      f.findingType === 'SUSPENSION' || f.findingType === 'LICENSE_ACTION',
    formatExistence: (findings) => {
      const susp = findings.filter((f) => f.findingType === 'SUSPENSION').length
      const act = findings.filter(
        (f) => f.findingType === 'LICENSE_ACTION'
      ).length
      const parts: string[] = []
      if (susp > 0) {
        parts.push(`${susp} ${susp === 1 ? 'suspension' : 'suspensions'}`)
      }
      if (act > 0) {
        parts.push(
          `${act} ${act === 1 ? 'license action' : 'license actions'}`
        )
      }
      return parts.join(', ')
    },
    sourceLabel: 'MVR',
  },
]
